import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { BRAND, CONTACT_INFO } from '../data/mock';
import { ArrowUp, Eye, Instagram, Mail, Phone } from 'lucide-react';

const Footer = () => {
  const { language } = useLanguage();
  const [showBackToTop, setShowBackToTop] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const content = {
    es: {
      tagline: 'Especialista en desarrollo web premium',
      quickLinks: 'Enlaces Rápidos',
      legal: 'Legal',
      contact: 'Contacto',
      social: 'Instagram',
      adminAccess: 'Acceso admin',
      copyright: '© 2025 IllumiDev. Todos los derechos reservados.',
      links: {
        home: 'Inicio',
        services: 'Servicios',
        packages: 'Paquetes',
        about: 'Valores',
        blog: 'Blog',
        contact: 'Contacto'
      },
      legalLinks: {
        privacy: 'Privacidad',
        terms: 'Términos',
        refund: 'Reembolsos'
      }
    },
    en: {
      tagline: 'Premium web development specialist',
      quickLinks: 'Quick Links',
      legal: 'Legal',
      contact: 'Contact',
      social: 'Instagram',
      adminAccess: 'Admin access',
      copyright: '© 2025 IllumiDev. All rights reserved.',
      links: {
        home: 'Home',
        services: 'Services',
        packages: 'Packages',
        about: 'About',
        blog: 'Blog',
        contact: 'Contact'
      },
      legalLinks: {
        privacy: 'Privacy',
        terms: 'Terms',
        refund: 'Refunds'
      }
    }
  };

  const t = content[language];

  return (
    <>
      <footer className="footer">
        <div className="container">
          {/* Founder Section at Top of Footer */}
          <div className="footer-founder">
            <h2 className="founder-name">Sergio D.</h2>
            <div className="footer-founder-avatar">
              <img src={BRAND.founderPhotoUrl} alt="Sergio D." />
            </div>
            <div className="footer-founder-info">
              <h3>{language === 'es' ? 'Tu Especialista en Desarrollo Web' : 'Your Web Development Specialist'}</h3>
              <p>{language === 'es' ? 'Construyendo experiencias digitales premium que impulsan resultados reales.' : 'Building premium digital experiences that drive real results.'}</p>
            </div>
          </div>

          <div className="footer-content">
            <div className="footer-col footer-brand">
              <div className="logo">
                <img src={BRAND.logoUrl} alt={BRAND.name} />
              </div>
              <p data-testid="footer-tagline-text">{t.tagline}</p>
            </div>

            <div className="footer-col">
              <h4>{t.quickLinks}</h4>
              <ul>
                <li><a href="#hero">{t.links.home}</a></li>
                <li><a href="#services">{t.links.services}</a></li>
                <li><a href="#packages">{t.links.packages}</a></li>
                <li><a href="#values">{t.links.about}</a></li>
                <li><a href="/blog" data-testid="footer-blog-link">{t.links.blog}</a></li>
                <li><a href="#contact">{t.links.contact}</a></li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>{t.legal}</h4>
              <ul>
                <li><a href="/privacy" data-testid="footer-privacy-link">{t.legalLinks.privacy}</a></li>
                <li><a href="/terms" data-testid="footer-terms-link">{t.legalLinks.terms}</a></li>
                <li><a href="/refunds" data-testid="footer-refunds-link">{t.legalLinks.refund}</a></li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>{t.contact}</h4>
              <ul className="footer-contact">
                <li>
                  <Mail size={16} />
                  <a href={`mailto:${CONTACT_INFO.email}`} data-testid="footer-email-link">{CONTACT_INFO.email}</a>
                </li>
                <li>
                  <Phone size={16} />
                  <a href={`tel:${CONTACT_INFO.phone}`} data-testid="footer-phone-link">{CONTACT_INFO.phoneDisplay}</a>
                </li>
                <li>
                  <Instagram size={16} />
                  <a href={CONTACT_INFO.instagramUrl} target="_blank" rel="noreferrer" data-testid="footer-instagram-link">
                    @{CONTACT_INFO.instagramHandle}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>{t.copyright}</p>
            <a href="/admin/login" className="footer-admin-eye" aria-label={t.adminAccess} data-testid="footer-admin-eye-link">
              <Eye size={16} />
            </a>
          </div>
        </div>
      </footer>

      {showBackToTop && (
        <button 
          className="back-to-top"
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          <ArrowUp size={20} />
        </button>
      )}
    </>
  );
};

export default Footer;
